import { findIndex } from 'lodash';
import loadable from '@loadable/component';
import Contacts from 'parma-theme/components/views/Contacts/Contacts';
import SearchHomepage from 'parma-theme/components/views/SearchHomepage/SearchHomepage';

import {
  addCardWithImageTemplateOptions as base_addCardWithImageTemplateOptions,
  addInEvidenceTemplateOptions as base_addInEvidenceTemplateOptions,
  addDefaultOptions,
  addLinkMoreOptions,
} from 'design-comuni-plone-theme/config/Blocks/ListingOptions';

import { addShowTagsOption } from 'parma-theme/config/ListingOptions/showTags';

import SimpleListTemplateCosaServe from 'parma-theme/components/Blocks/Listing/SimpleListTemplateCosaServe';
import SimpleListTemplateSkeletonCosaServe from 'parma-theme/components/Blocks/Listing/TemplatesSkeletons/SimpleListTemplateSkeletonCosaServe';
import CardWithImageRssTemplateSkeleton from 'design-comuni-plone-theme/components/ItaliaTheme/Blocks/RssBlock/TemplatesSkeleton/CardWithImageRssTemplateSkeleton';
import ParmaEventsCard from 'parma-theme/components/Blocks/RssBlock/ParmaEventsCard';
import RSSMeteoCard from 'parma-theme/components/Blocks/RssBlock/RSSMeteoCard';
// import RSSMeteoCardSkeleton from 'parma-theme/components/Blocks/RssBlock/TemplatesSkeleton/RSSMeteoCardSkeleton';

import reducers from 'parma-theme/reducers';

const applyConfig = (config) => {
  const isSearchSite =
    (__CLIENT__ && !!window.env.RAZZLE_SOLR_SEARCH_SITE) ||
    !!process.env.RAZZLE_SOLR_SEARCH_SITE ||
    false;

  const enableWorkingCopy =
    (__CLIENT__ && !!window.env.RAZZLE_ENABLE_WORKING_COPY) ||
    !!process.env.RAZZLE_ENABLE_WORKING_COPY ||
    false;

  const linkAreaPersonale =
    (__CLIENT__ && window.env.RAZZLE_LINK_AREA_PERSONALE) ||
    process.env.RAZZLE_LINK_AREA_PERSONALE ||
    '';

  config.settings = {
    ...config.settings,
    isMultilingual: !isSearchSite,
    supportedLanguages: ['en', 'it'],
    defaultLanguage: 'it',
    maxFileUploadSize: 367001600, // 104857600 * 3.5, // 300MB
    // matomoSiteId: 'xxxxxxxxx',
    // matomoUrlBase: 'https://ingestion.webanalytics.italia.it/',
    // italiaThemeViewsConfig: {
    //   ...config.settings.italiaThemeViewsConfig,
    //   imagePosition: 'afterHeader', // possible values: afterHeader, documentBody
    // },
    siteProperties: {
      /*
        Per tutte le props di siteProperties, può essere definito un oggetto per i siti multilingua, strutturato in questo modo:
        {'default': _valore_della_prop, 'it': _valore_della_prop,...}
        Se l'oggetto non è strutturato in questo modo, viene usato l'oggetto stesso, altrimenti viene preso il valore della corrispondente alla prop della lingua corrente o alla prop 'default' se esiste.
      */
      ...config.settings.siteProperties,
      siteTitle: 'Comune di Parma',
      siteSubtitle: '',
      parentSiteTitle: 'Regione Emilia-Romagna',
      parentSiteURL: 'https://www.regione.emilia-romagna.it/',
      subsiteParentSiteTitle: 'Comune di Parma',
      arLoginUrl: linkAreaPersonale,
      markSpecialLinks: false,
      markFooterLinks: false,
      userLoggedMenu: [
        {
          href: { it: linkAreaPersonale },
          title: {
            it: "Accedi all'area personale",
            en: 'Login to your personal area',
          },
          roles: [],
          className: 'link-areaPersonale',
        },
      ],
    },
    externalRoutes: [
      ...config.settings.externalRoutes,
      // { match: '/comune/Amministrazione-Trasparente' },
      { match: '/comune' },
      { match: '/consigliocomunale' },
      { match: '/societapartecipate' },
      { match: '/notizie' },
      { match: '/qualita' },
      { match: '/sport' },
      { match: '/protezionecivile' },
      { match: '/santilario' },
      { match: '/demografici' },
      { match: '/mobilita' },
      { match: '/parmaforexpats' },
      { match: '/benessereanimale' },
      { match: '/coronavirus' },
      { match: '/informanziani' },
      { match: '/parmasalute' },
      { match: '/percorsonascita' },
      { match: '/ambiente' },
      { match: '/pianoenergiaclima' },
      { match: '/verde-pubblico' },
      { match: '/centroperlefamiglie' },
      { match: '/partecipazione' },
      { match: '/elezioniccv' },
      { match: '/associazionismo' },
      { match: '/welfare' },
      { match: '/serviziocivile' },
      { match: '/giovani-a-parma' },
      { match: '/informastranieri' },
      { match: '/attivitaeconomiche' },
      { match: '/pianificazioneterritoriale' },
      { match: '/pianificazioneterritoriale/mappeinterattive' },
      { match: '/poliziamunicipale' },
      { match: '/servizieducativi' },
      { match: '/sportelloscuola' },
      { match: '/orientamente' },
      { match: '/tirocini' },
      { match: '/cultura' },
      { match: '/pariopportunita' },
      { match: '/tributi-entrate' },
      { match: '/prenota' },
      { match: '/archivio' },
    ],
    loadables: {
      ...config.settings.loadables,
      'subsite-cultura': loadable.lib(() => import('./subsites/cultura')),
      'subsite-diritti': loadable.lib(() => import('./subsites/diritti')),
      'subsite-giovani': loadable.lib(() => import('./subsites/giovani')),
      'subsite-polizia-municipale': loadable.lib(() =>
        import('./subsites/polizia-municipale'),
      ),
      'subsite-scuola': loadable.lib(() => import('./subsites/scuola')),
      'subsite-suei': loadable.lib(() => import('./subsites/suei')),
    },
    hasWorkingCopySupport: enableWorkingCopy,
    'volto-gdpr-privacy': {
      ...config.settings['volto-gdpr-privacy'],
      defaultPanelConfig: {
        ...config.settings['volto-gdpr-privacy'].defaultPanelConfig,
        last_updated: '2022-07-06T07:18:00+00:00',
        text: {
          it: {
            title: 'Usiamo i cookie',
            description:
              "Questo sito utilizza i cookie tecnici di navigazione e di sessione per garantire un miglior servizio di navigazione del sito, e cookie analitici per raccogliere informazioni sull'uso del sito da parte degli utenti.  Utilizza anche cookie di profilazione dell'utente per fini statistici. Per i cookie di profilazione puoi decidere se abilitarli o meno cliccando sul pulsante 'Impostazioni'. Per saperne di più, su come disabilitare i cookie oppure abilitarne solo alcuni, consulta la nostra <a href='/it/informazioni-generali/cookie-policy-del-portale-del-comune-di-parma'>Cookie Policy</a>.",
          },
          en: {
            title: 'We use cookies',
            description:
              "This site uses technical navigation and session cookies to ensure a better site navigation service, and analytical cookies to collect information on the use of the site by users. It also uses user profiling cookies for statistical. For profiling cookies you can decide whether to enable them or not by clicking on the 'Settings' button. To find out more, on how to disable cookies or enable only some of them, consult our <a href='/en/privacy-policy'>Cookie Policy</a>.",
          },
        },
        technical: {
          ...config.settings['volto-gdpr-privacy'].defaultPanelConfig.technical,
          choices: [
            {
              config_key: 'MATOMO',
              text: {
                it: {
                  title: 'Matomo',
                  description:
                    "I cookie di Matomo sono usati per analizzare la navigazione sul sito al fine di migliorarla e fornire all'utente un'esperienza di navigazione migliore possibile.",
                },
                en: {
                  title: 'Matomo',
                  description:
                    'Matomo cookies are used to analyze navigation on the site in order to improve it and provide the user with the best possible browsing experience.',
                },
                // fr: {
                //   title: 'Matomo',
                //   description:
                //     "Les cookie de Matomo sont utilisés pour analyser la navigation sur le site afin de l'améliorer et offrir à l'utilisateur la meilleure expérience de navigation possible.",
                // },
              },
            },
            ...config.settings[
              'volto-gdpr-privacy'
            ].defaultPanelConfig.technical.choices.filter(
              (f) => f.config_key !== 'GANALYTICS',
            ),
          ],
        },
      },
    },
  };

  config.views = {
    ...config.views,
    layoutViews: {
      ...config.views.layoutViews,
      contacts_view: Contacts,
      search_homepage: SearchHomepage,
    },
    layoutViewsNamesMapping: {
      ...config.views.layoutViewsNamesMapping,
      contacts_view: 'Contatti',
      search_homepage: 'Ricerca homepage',
    },
  };

  const cardWithImageTemplateIndex = findIndex(
    config.blocks.blocksConfig.listing.variations,
    (variation) => variation.id === 'cardWithImageTemplate',
  );

  config.blocks.blocksConfig.listing.variations[
    cardWithImageTemplateIndex
  ].schemaEnhancer = ({ schema, formData, intl }) => {
    let pos = base_addCardWithImageTemplateOptions(schema, formData, intl);
    addShowTagsOption(schema, formData, intl, pos);
    addLinkMoreOptions(schema, formData, intl);
    return schema;
  };

  const inEvidenceTemplateIndex = findIndex(
    config.blocks.blocksConfig.listing.variations,
    (variation) => variation.id === 'inEvidenceTemplate',
  );

  config.blocks.blocksConfig.listing.variations[
    inEvidenceTemplateIndex
  ].schemaEnhancer = ({ schema, formData, intl }) => {
    let pos = base_addInEvidenceTemplateOptions(schema, formData, intl);
    addShowTagsOption(schema, formData, intl, pos);
    addLinkMoreOptions(schema, formData, intl);
    return schema;
  };
  // config.blocks.blocksConfig.listing = {
  //   ...config.blocks.blocksConfig.listing,
  //   variations: [
  //     ...config.blocks.blocksConfig.listing.variations,
  //     // {
  //     //   id: 'cardWithSideImageTemplate',
  //     //   isDefault: false,
  //     //   title: 'Card con immagine affiancata',
  //     //   template: CardWithSideImageTemplate,
  //     //   skeleton: CardWithSideImageTemplateSkeleton,
  //     //   schemaEnhancer: ({ schema, formData, intl }) => {
  //     //     let pos = addDefaultOptions(schema, formData, intl);
  //     //     addCardWithSideImageTemplateOptions(schema, formData, intl, pos);
  //     //     return schema;
  //     //   },
  //     // },
  //   ],
  //   listing_bg_colors: [], //{name:'blue', label:'Blu'},{name:'light-blue', label:'Light blue'},{name:'sidebar-background', label:'Grey'}
  //   listing_items_colors: [], //{name:'blue', label:'Blu'},{name:'light-blue', label:'Light blue'},{name:'sidebar-background', label:'Grey'}
  // };

  config.blocks.blocksConfig.listing.variations = [
    ...config.blocks.blocksConfig.listing.variations,
    {
      id: 'listing_services_cosa_serve',
      isDefault: false,
      fullobjects: 1,
      title: 'Lista Servizi / Cosa Serve',
      template: SimpleListTemplateCosaServe,
      skeleton: SimpleListTemplateSkeletonCosaServe,
      schemaEnhancer: ({ schema, formData, intl }) => {
        addDefaultOptions(schema, formData, intl);
        return schema;
      },
    },
  ];

  config.blocks.blocksConfig.rssBlock.templates.parma_events_with_image = {
    label: 'Card per eventi',
    template: ParmaEventsCard,
    skeleton: CardWithImageRssTemplateSkeleton,
  };
  config.blocks.blocksConfig.rssBlock.templates.parma_rss_meteo = {
    label: 'Card allerte meteo',
    template: RSSMeteoCard,
    // skeleton: RSSMeteoCardSkeleton,
  };

  // REDUCERS
  config.addonReducers = {
    ...config.addonReducers,
    ...reducers,
  };

  return config;
};

export default applyConfig;
